import { useState, useEffect } from "react"
import styled from "styled-components"
import Fieldset from "components/_UI/Fieldset"
import colors from "theme/colors"
import masks from "helpers/masks"
import date from "helpers/date"
import fieldValidations from "helpers/fieldValidations"
import fieldMessages from "texts/messages/field"
import { AlertCircle, Info } from 'lucide-react'
import dayjs from "dayjs"

/**
 * Datas de venda do ingresso.
 *
 * Com `startOnly`, só o início (o ingresso por lote que abre a venda numa data):
 * sai `sale_end: null`, e apagar ou invalidar um dos dois campos devolve
 * `sale_start: null`, para o formulário não salvar a data anterior sem querer.
 */
export default function SalePeriodForm({
    salePeriod,
    startOnly = false,
    onChangeSalePeriod = () => {}
}) {
    const [saleStartDate, setSaleStartDate] = useState()
    const [saleStartTime, setSaleStartTime] = useState()
    const [saleEndDate, setSaleEndDate] = useState()
    const [saleEndTime, setSaleEndTime] = useState()

    const [saleStartDateError, setSaleStartDateError] = useState()
    const [saleStartTimeError, setSaleStartTimeError] = useState()
    const [saleEndDateError, setSaleEndDateError] = useState()
    const [saleEndTimeError, setSaleEndTimeError] = useState()
    const [crossDateError, setCrossDateError] = useState('')

    const [syncProp, setSyncProp] = useState(true)

    function getFilledCount() {
        let count = 0
        if (saleStartDate) count++
        if (saleStartTime) count++
        if (saleEndDate) count++
        if (saleEndTime) count++
        return count
    }

    function getIncompleteHint() {
        const filled = getFilledCount()
        const total = startOnly ? 2 : 4
        if (filled === 0 || filled === total) return ''
        if (startOnly) return `Preencha a data e o horário de início (${filled}/2 preenchidos)`
        return `Preencha todos os campos do período (${filled}/4 preenchidos)`
    }

    function clearStartOnly() {
        if (startOnly) {
            onChangeSalePeriod({ sale_start: null, sale_end: null })
        }
    }

    function startHasPassed() {
        if (!startOnly || !saleStartDate || !saleStartTime || saleStartDateError || saleStartTimeError) return false
        const start = dayjs(`${date.unformat(saleStartDate)} ${saleStartTime}`)
        return start.isValid() && start.isBefore(dayjs())
    }

    function validateDateRange(startDate, startTime, endDate, endTime) {
        if (!startDate || !startTime || !endDate || !endTime) return true
        try {
            const unformattedStart = date.unformat(startDate)
            const unformattedEnd = date.unformat(endDate)
            const start = dayjs(`${unformattedStart} ${startTime}`)
            const end = dayjs(`${unformattedEnd} ${endTime}`)
            if (end.isBefore(start) || end.isSame(start)) {
                return 'A data/hora de fim deve ser posterior à data/hora de início'
            }
            return ''
        } catch {
            return ''
        }
    }

    function tryPropagate(newStartDate, newStartTime, newEndDate, newEndTime) {
        if (startOnly) {
            onChangeSalePeriod({
                sale_start: `${date.unformat(newStartDate)} ${newStartTime}:00`,
                sale_end: null
            })
            return
        }

        if (!newStartDate || !newStartTime || !newEndDate || !newEndTime) {
            return
        }

        const crossError = validateDateRange(newStartDate, newStartTime, newEndDate, newEndTime)
        setCrossDateError(crossError)
        if (crossError) return

        const unformattedStartDate = date.unformat(newStartDate)
        const unformattedEndDate = date.unformat(newEndDate)
        onChangeSalePeriod({
            sale_start: `${unformattedStartDate} ${newStartTime}:00`,
            sale_end: `${unformattedEndDate} ${newEndTime}:00`
        })
    }

    function changeSaleStartDate(newDate) {
        setSaleStartDate(newDate)

        const dateValidation = fieldValidations.date().validate.validDateRange(newDate)
        if (dateValidation !== true) {
            setSaleStartDateError(dateValidation)
            clearStartOnly()
            return
        }

        setSaleStartDateError('')

        if (!saleStartTime || saleStartTimeError) {
            clearStartOnly()
            return
        }

        tryPropagate(newDate, saleStartTime, saleEndDate, saleEndTime)
    }

    function changeSaleStartTime(newTime) {
        setSaleStartTime(newTime)

        if (!fieldValidations.time().pattern.value.test(newTime)) {
            setSaleStartTimeError(fieldMessages.invalidTime('início'))
            clearStartOnly()
            return
        }

        setSaleStartTimeError('')

        if (!saleStartDate || saleStartDateError) {
            clearStartOnly()
            return
        }

        tryPropagate(saleStartDate, newTime, saleEndDate, saleEndTime)
    }

    function changeSaleEndDate(newDate) {
        setSaleEndDate(newDate)

        const dateValidation = fieldValidations.date().validate.validDateRange(newDate)
        if (dateValidation !== true) {
            setSaleEndDateError(dateValidation)
            return
        }

        setSaleEndDateError('')

        if (!saleEndTime || saleEndTimeError) return

        tryPropagate(saleStartDate, saleStartTime, newDate, saleEndTime)
    }

    function changeSaleEndTime(newTime) {
        setSaleEndTime(newTime)

        if (!fieldValidations.time().pattern.value.test(newTime)) {
            setSaleEndTimeError(fieldMessages.invalidTime('fim'))
            return
        }

        setSaleEndTimeError('')

        if (!saleEndDate || saleEndDateError) return

        tryPropagate(saleStartDate, saleStartTime, saleEndDate, newTime)
    }

    useEffect(() => {
        if (!salePeriod?.sale_start || (!startOnly && !salePeriod?.sale_end)) {
            return
        }

        const { sale_start, sale_end } = salePeriod
        const [startDate, startTime] = sale_start.split(' ')
        setSaleStartDate(date.format(startDate))
        setSaleStartTime(startTime.substring(0, startTime.length - 3))

        if (startOnly) {
            return
        }

        const [endDate, endTime] = sale_end.split(' ')
        setSaleEndDate(date.format(endDate))
        setSaleEndTime(endTime.substring(0, endTime.length - 3))
    }, [])

    const incompleteHint = getIncompleteHint()

    return (
        <PeriodContainer>
            <PeriodGrid>
                <FieldWrapper>
                    <Fieldset
                        syncProp={syncProp}
                        layout='squired'
                        label='Data de início'
                        name='sale_start_day'
                        mask={masks.date}
                        validations={fieldValidations.date()}
                        value={saleStartDate}
                        onChangeValue={changeSaleStartDate}
                    />
                    {saleStartDateError && (
                        <FieldError>
                            <AlertCircle size={12} />
                            {saleStartDateError}
                        </FieldError>
                    )}
                </FieldWrapper>
                <FieldWrapper>
                    <Fieldset
                        syncProp={syncProp}
                        layout='squired'
                        label='Horário de início'
                        name='sale_start_time'
                        mask={masks.time}
                        validations={fieldValidations.time('Horário de início')}
                        value={saleStartTime}
                        onChangeValue={changeSaleStartTime}
                    />
                    {saleStartTimeError && (
                        <FieldError>
                            <AlertCircle size={12} />
                            {saleStartTimeError}
                        </FieldError>
                    )}
                </FieldWrapper>
                {!startOnly && (
                    <>
                        <FieldWrapper>
                            <Fieldset
                                syncProp={syncProp}
                                layout='squired'
                                label='Data de fim'
                                mask={masks.date}
                                name='sale_end_day'
                                validations={fieldValidations.date()}
                                value={saleEndDate}
                                onChangeValue={changeSaleEndDate}
                            />
                            {saleEndDateError && (
                                <FieldError>
                                    <AlertCircle size={12} />
                                    {saleEndDateError}
                                </FieldError>
                            )}
                        </FieldWrapper>
                        <FieldWrapper>
                            <Fieldset
                                syncProp={syncProp}
                                layout='squired'
                                label='Horário de fim'
                                mask={masks.time}
                                name='sale_end_time'
                                validations={fieldValidations.time('Horário de fim')}
                                value={saleEndTime}
                                onChangeValue={changeSaleEndTime}
                            />
                            {saleEndTimeError && (
                                <FieldError>
                                    <AlertCircle size={12} />
                                    {saleEndTimeError}
                                </FieldError>
                            )}
                        </FieldWrapper>
                    </>
                )}
            </PeriodGrid>

            {crossDateError && (
                <CrossDateError>
                    <AlertCircle size={12} />
                    {crossDateError}
                </CrossDateError>
            )}

            {incompleteHint && (
                <IncompleteHint>
                    <Info size={12} />
                    {incompleteHint}
                </IncompleteHint>
            )}

            {startHasPassed() && (
                <IncompleteHint>
                    <Info size={12} />
                    Essa data já passou: o ingresso vende assim que for salvo.
                </IncompleteHint>
            )}
        </PeriodContainer>
    )
}

const PeriodContainer = styled.div`
    width: 100%;
`;

const PeriodGrid = styled.div`
    display: grid;
    gap: 10px;
    grid-template-columns: 1fr 1fr;
`;

const FieldWrapper = styled.div`
    width: 100%;
`;

const FieldError = styled.span`
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    font-weight: 500;
    color: ${colors.danger};
    margin-top: 4px;
    padding: 3px 6px;
    background: ${colors.danger}0A;
    border-radius: 4px;
    line-height: 1.4;
`;

const CrossDateError = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 500;
    color: ${colors.danger};
    background: ${colors.danger}0A;
    border-radius: 6px;
    padding: 8px 10px;
    margin-top: 8px;
`;

const IncompleteHint = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 500;
    color: ${colors.occasionalOrange};
    background: ${colors.occasionalOrange}0A;
    border-radius: 6px;
    padding: 8px 10px;
    margin-top: 8px;
`;
